import React from 'react'
import { useTranslation } from 'react-i18next'
import emojiRegex from 'emoji-regex'
// @ts-ignore
import { SimpleBar } from '@netbasequid/chart'
import { useDisplayTheme } from 'hooks/useUiState'
import { getTwEmoji } from 'utils/emojiUtil'
import { useEmojiNameToDisplayMap } from 'hooks/useEmojiState'
import InsightType from 'enums/InsightType'
import {
  BaseInsightChartProps,
  SideBySideSimplifiedData,
  SideBySideSelectionType
} from '../../types'
import {
  getDisplayInsightValue,
  getColorByCountType,
  getSortedSetBreakInfo,
  getPercentageByIndexandBreakInfo
} from '../utils'
import {
  InsightChartContainer,
  InsightContainer,
  LabelContainer,
  LabelNameWithTooltip,
  LabelValue
} from './styles'

type PositiveSentiment = 'Likes' | 'PositiveEmotions' | 'PositiveBehaviors'

const getSetInfo = (simplifiedData: SideBySideSimplifiedData, totalBars: number) => {
  const { set, topicId, topicName, themeId, themeName, tagName, docName } = simplifiedData
  const sortedSet = [...set].sort((a, b) => b.value! - a.value!)

  const breakInfo = getSortedSetBreakInfo(sortedSet)

  const setInfos: SideBySideSelectionType[] = sortedSet.map((data, idx) => {
    const percentage = getPercentageByIndexandBreakInfo(
      {
        value: data.value!,
        totalBars,
        index: idx
      },
      breakInfo
    )

    const name = data.name ? data.name : ''
    return {
      name,
      value: data.value!,
      insightType: data.insightType!,
      percentage,
      insight: data,
      topicId,
      topicName,
      themeId,
      themeName,
      tagName,
      docName
    }
  })

  return {
    hasBreakSymbol: breakInfo.hasBreakSymbol,
    setInfos
  }
}

export const getEmoji = (insightName: string, emojiNameToDisplayMap: Record<string, string>) => {
  const insightEmoji = emojiNameToDisplayMap[insightName]
  if (insightEmoji) {
    return getTwEmoji(insightEmoji) || insightEmoji
  }
  return insightName
}

const getColorByInsightType = (insightType: PositiveSentiment) => {
  const { LIKES, POSITIVE_EMOTIONS, POSITIVE_BEHAVIORS } = InsightType
  const positiveSentiment = [LIKES, POSITIVE_EMOTIONS, POSITIVE_BEHAVIORS]
  return positiveSentiment.includes(insightType) ? 'semantic.positive' : 'semantic.negative'
}

const BaseInsightChart = ({
  data,
  countType,
  isEmoji,
  isSentiment,
  totalBars,
  onCellClick
}: BaseInsightChartProps) => {
  const theme = useDisplayTheme()
  const emojiNameToDisplayMap = useEmojiNameToDisplayMap()
  const { t } = useTranslation()

  if (!data) return null
  const { hasBreakSymbol, setInfos } = getSetInfo(data, totalBars)
  const noInfos = new Array(totalBars - setInfos.length).fill(1)

  const getLabelName = (insightName: string) => {
    if (isEmoji) {
      return getEmoji(insightName, emojiNameToDisplayMap)
    }
    return emojiRegex().test(insightName) ? getTwEmoji(insightName) : insightName
  }

  return (
    <InsightChartContainer>
      {setInfos?.map((insightData) => (
        <InsightContainer
          key={`${insightData.name}-${insightData.insightType}`}
          mode={theme}
          clickable={insightData.value > 0}>
          <LabelContainer onClick={() => onCellClick(insightData)}>
            <LabelNameWithTooltip>{getLabelName(insightData.name!)}</LabelNameWithTooltip>
            <LabelValue>{getDisplayInsightValue(insightData.value!)}</LabelValue>
          </LabelContainer>
          <SimpleBar
            progress={insightData.percentage}
            colorSetting={
              isSentiment
                ? getColorByInsightType(insightData.insightType! as PositiveSentiment)
                : getColorByCountType(countType)
            }
            mode={theme}
            breakSymbolType="BreakDoubleSlash100"
            breakSymbolPosition={hasBreakSymbol ? 70 : undefined}
            breakSymbolTooltipContent={t('breakSymbolTooltip')}
            height="2px"
          />
        </InsightContainer>
      ))}
      {noInfos?.map((_, idx) => (
        // eslint-disable-next-line react/no-array-index-key
        <InsightContainer key={`no-data-${idx}`} mode={theme}>
          <LabelContainer>
            <LabelNameWithTooltip type="placeholder">—</LabelNameWithTooltip>
            <LabelValue type="placeholder">—</LabelValue>
          </LabelContainer>
          <SimpleBar progress={0} mode={theme} height="2px" />
        </InsightContainer>
      ))}
    </InsightChartContainer>
  )
}
export default BaseInsightChart
